<template>
  <div class="form-group" :class="{ 'has-error': hasError }">
    <label v-if="label" :for="inputId" class="form-label">
      {{ label }}
      <span v-if="required" class="required-mark">*</span>
      <span v-if="notes" class="notes-remark">{{ notes }}</span>
      <br />
      <span v-if="requireSmsText" class="notes-remark text-danger">* Each SMS credit corresponds to 160 characters, excluding special characters. To verify your usage credits, check at this <a target="_blank" href="https://sakari.io/sms-length-calculator">link</a>. Keep in mind, one segment equals one credit.</span>

    </label>

    <textarea
      v-if="type === 'textarea'"
      :value="value"
      @input="handleInput"
      @blur="handleBlur"
      :id="inputId"
      :name="name || inputId"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :class="[
        'form-control',
        // { 'is-invalid': hasError },
        // { 'is-valid': touched && !hasError }
      ]"
      :maxlength="maxLength"
      ref="input"
      v-bind="$attrs"
    ></textarea>

    <input
      v-else
      :value="value"
      @input="handleInput"
      @blur="handleBlur"
      :id="inputId"
      :name="name || inputId"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :type="type"
      :class="[
        'form-control',
        // { 'is-invalid': hasError },
        // { 'is-valid': touched && !hasError }
      ]"
      :maxlength="maxLength"
      ref="input"
      v-bind="$attrs"
    />

    <small v-if="hint" class="form-text text-muted">{{ hint }}</small>
    <div v-if="hasError" class="invalid-feedback">{{ errorMessage || innerError }}</div>
  </div>
</template>

<script>
export default {
  name: 'InputLabel',
  
  inheritAttrs: false,

  props: {
    label: {
      type: String,
      default: null
    },
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: function(value) {
        return [
          'text', 'password', 'email', 'number', 
          'tel', 'url', 'search', 'textarea', 'date', 'time'
        ].includes(value);
      }
    },
    placeholder: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: null
    },
    pattern: {
      type: [String, RegExp],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: ''
    },
    validateOnBlur: {
      type: Boolean,
      default: true
    },
    min: {
      type: [Number, String],
      default: null
    },
    max: {
      type: [Number, String],
      default: null
    },
    rows: {
      type: [Number, String],
      default: 3
    },
    notes: {
      type: String,
      default: null
    },
    requireSmsText: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      touched: false,
      innerError: ''
    }
  },

  computed: {
    inputId() {
      return this.name || 
        `input-${this.label ? this.label.toLowerCase().replace(/\s+/g, '-') : this._uid}`;
    },

    hasError() {
      return !!(this.errorMessage || this.innerError);
    }
  },

  methods: {
    handleInput(event) {
      let value = event.target.value;
      
      
      if (this.type === 'number' && value !== '') {
        value = value === '' ? '' : Number(value);
        
        
        if (this.min !== null && value < Number(this.min)) {
          value = Number(this.min);
        }
        if (this.max !== null && value > Number(this.max)) {
          value = Number(this.max);
        }
      }
      
      this.$emit('input', value);
      
      if (!this.validateOnBlur) {
        this.validate(value);
      }
    },

    handleBlur(event) {
      this.touched = true;
      if (this.validateOnBlur) {
        this.validate(event.target.value);
      }
      this.$emit('blur', event);
    },

    validate(value) {
      this.innerError = '';
      
      
      if (this.required && (value === null || value === undefined || value === '')) {
        this.innerError = 'This field is required';
        return false;
      }

      
      if (this.type !== 'number' && this.pattern && value) {
        const regex = this.pattern instanceof RegExp 
          ? this.pattern 
          : new RegExp(this.pattern);
        if (!regex.test(value)) {
          this.innerError = 'Invalid format';
          return false;
        }
      }

      
      switch (this.type) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (value && !emailRegex.test(value)) {
            this.innerError = 'Invalid email address';
            return false;
          }
          break;
        case 'number':
          if (value !== '' && isNaN(Number(value))) {
            this.innerError = 'Must be a valid number';
            return false;
          }
          
          const numValue = Number(value);
          if (value !== '' && this.min !== null && numValue < Number(this.min)) {
            this.innerError = `Value must be greater than or equal to ${this.min}`;
            return false;
          }
          if (value !== '' && this.max !== null && numValue > Number(this.max)) {
            this.innerError = `Value must be less than or equal to ${this.max}`;
            return false;
          }
          break;
      }

      return true;
    },

    focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },

    reset() {
      this.touched = false;
      this.innerError = '';
      this.$emit('input', this.type === 'number' ? null : '');
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: inline-block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.required-mark {
  color: #dc3545;
  margin-left: 0.25rem;
}

.notes-remark {
  margin-left: 0.25rem;
  font-style: italic;
}

.form-control {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

textarea.form-control {
  min-height: 100px;
  resize: vertical;
}

.form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control.is-valid {
  border-color: #28a745;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.form-text {
  display: block;
  margin-top: 0.25rem;
  font-size: 80%;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #e9ecef;
  opacity: 1;
}
</style>
